import { useTranslation } from "react-i18next";
import { useCart } from "@/hooks/useCart";

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CartDrawer({ open, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { items, removeFromCart, updateQuantity, cartTotal, clearCart } = useCart();
  const isFr = i18n.language === "fr";

  const handleWhatsAppCheckout = () => {
    if (items.length === 0) return;
    const lines = items.map(
      (item) =>
        `• ${isFr ? item.nameF : item.name} x${item.quantity} = ${(item.price * item.quantity).toLocaleString()} XAF`
    );
    const total = `\nTotal: ${cartTotal.toLocaleString()} XAF`;
    const msg = encodeURIComponent(
      `Hello Creative Hub! I'd like to order:\n\n${lines.join("\n")}${total}\n\nPlease confirm availability and delivery details.`
    );
    window.open(`https://wa.me/237678012159?text=${msg}`, "_blank");
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-50 transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-96 bg-white z-50 shadow-2xl flex flex-col transition-transform duration-400 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-rose-100">
          <h2
            className="text-xl font-bold text-stone-800"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("cart_title")}
          </h2>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-amber-50 text-gray-500 hover:text-amber-700 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-rose-50">
                <i className="ri-shopping-bag-line text-4xl text-amber-300"></i>
              </div>
              <p className="text-gray-500 text-sm">{t("cart_empty")}</p>
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-gradient-to-r from-amber-600 to-rose-600 text-white text-sm font-semibold rounded-full hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                {t("cart_continue")}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3 p-3 rounded-xl bg-amber-50/50 border border-amber-100">
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={item.image} alt={isFr ? item.nameF : item.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">
                      {isFr ? item.nameF : item.name}
                    </p>
                    <p className="text-amber-700 font-bold text-sm mt-0.5">
                      {(item.price * item.quantity).toLocaleString()} XAF
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-white border border-amber-200 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer text-xs font-bold"
                      >
                        −
                      </button>
                      <span className="text-sm font-semibold w-5 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-6 h-6 flex items-center justify-center rounded-full bg-white border border-amber-200 text-amber-700 hover:bg-amber-100 transition-colors cursor-pointer text-xs font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-rose-100 bg-white">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-600 font-medium">{t("cart_total")}</span>
              <span className="text-xl font-bold text-amber-800" style={{ fontFamily: "'Playfair Display', serif" }}>
                {cartTotal.toLocaleString()} XAF
              </span>
            </div>
            <button
              onClick={handleWhatsAppCheckout}
              className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-full hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              {t("cart_checkout")}
            </button>
            <button
              onClick={clearCart}
              className="w-full mt-2 py-2.5 text-sm text-gray-500 hover:text-red-500 transition-colors cursor-pointer"
            >
              {t("cart_remove")} all
            </button>
          </div>
        )}
      </div>
    </>
  );
}